// src/modules/Shop/components/ShopLayout.js
import React, { useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaShoppingCart,
  FaBars,
  FaTimes,
  FaSearch,
  FaUser,
  FaHeart,
  FaChevronDown,
  FaMapMarkerAlt,
  FaQuestionCircle
} from 'react-icons/fa';
import { useCart } from '../contexts/CartContext';
import CartDrawer from './CartDrawer';
import logoVega from '../../../assets/logovega.svg';

// ============================================
// STYLED COMPONENTS - Estilo Mercado Livre
// ============================================

const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
`;

// Top Bar - Links superiores
const TopBar = styled.div`
  background: #333;
  padding: 6px 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TopBarContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TopBarLinks = styled.div`
  display: flex;
  gap: 16px;

  a {
    color: rgba(255,255,255,0.8);
    text-decoration: none;
    font-size: 12px;
    transition: color 150ms;

    &:hover {
      color: #00D4AA;
    }
  }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  a {
    color: rgba(255,255,255,0.8);
    text-decoration: none;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: color 150ms;

    &:hover {
      color: #00D4AA;
    }

    svg {
      font-size: 11px;
    }
  }
`;

// Header Principal - Estilo ML
const Header = styled.header`
  background: linear-gradient(180deg, #1a1a1a 0%, #111 100%);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
`;

const HeaderMain = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    padding: 10px 12px;
    gap: 12px;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  flex-shrink: 0;

  img {
    height: 28px;

    @media (max-width: 768px) {
      height: 24px;
    }
  }
`;

const LogoBadge = styled.span`
  background: #00D4AA;
  color: #000;
  font-size: 9px;
  font-weight: 700;
  padding: 3px 6px;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Barra de Busca - Estilo ML (grande e proeminente)
const SearchContainer = styled.div`
  flex: 1;
  max-width: 600px;

  @media (max-width: 768px) {
    display: ${props => props.$mobileVisible ? 'block' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    padding: 12px;
    background: #111;
    max-width: none;
    border-top: 1px solid rgba(255,255,255,0.1);
  }
`;

const SearchBar = styled.form`
  display: flex;
  align-items: stretch;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);

  &:focus-within {
    box-shadow: 0 2px 8px rgba(0, 212, 170, 0.3);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  padding: 12px 16px;
  font-size: 15px;
  color: #333;
  min-width: 0;

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
  }
`;

const SearchButton = styled.button`
  background: #f0f0f0;
  border: none;
  padding: 0 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 150ms;
  border-left: 1px solid #e5e5e5;

  &:hover {
    background: #e5e5e5;
  }

  svg {
    font-size: 18px;
    color: #666;
  }
`;

// Acoes do Header (icones)
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const ActionItem = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 12px;
  color: rgba(255,255,255,0.85);
  text-decoration: none;
  border-radius: 4px;
  transition: all 150ms;
  min-width: 70px;

  &:hover {
    background: rgba(255,255,255,0.1);
    color: #00D4AA;
  }

  svg {
    font-size: 20px;
  }

  span {
    font-size: 10px;
    white-space: nowrap;
  }

  @media (max-width: 900px) {
    min-width: auto;
    padding: 8px;

    span {
      display: none;
    }
  }
`;

const CartButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 12px;
  color: rgba(255,255,255,0.85);
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 150ms;
  min-width: 70px;
  position: relative;

  &:hover {
    background: rgba(255,255,255,0.1);
    color: #00D4AA;
  }

  svg {
    font-size: 22px;
  }

  span {
    font-size: 10px;
    white-space: nowrap;
  }

  @media (max-width: 900px) {
    min-width: auto;
    padding: 8px;

    span {
      display: none;
    }
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 8px;
  background: #00D4AA;
  color: #000;
  font-size: 10px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;

  @media (max-width: 900px) {
    right: 0;
  }
`;

const MobileSearchToggle = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.85);
  padding: 8px;
  cursor: pointer;

  svg {
    font-size: 20px;
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.85);
  padding: 8px;
  cursor: pointer;

  svg {
    font-size: 22px;
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

// Barra de Categorias
const CategoriesBar = styled.nav`
  background: #222;
  border-top: 1px solid rgba(255,255,255,0.05);

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

const CategoriesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  align-items: center;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 8px 16px;
  }
`;

const CategoryLink = styled(Link)`
  color: rgba(255,255,255,0.75);
  text-decoration: none;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 150ms;
  border-bottom: 2px solid transparent;

  &:hover {
    color: #fff;
    background: rgba(255,255,255,0.05);
  }

  &.active {
    color: #00D4AA;
    border-bottom-color: #00D4AA;
  }

  @media (max-width: 768px) {
    padding: 14px 0;
    border-bottom: 1px solid rgba(255,255,255,0.1);

    &:last-child {
      border-bottom: none;
    }
  }
`;

const AllCategoriesButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255,255,255,0.9);
  background: transparent;
  border: none;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 150ms;

  &:hover {
    color: #00D4AA;
  }

  svg {
    font-size: 10px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// Banner de Promocao
const PromoBanner = styled.div`
  background: linear-gradient(90deg, #00D4AA 0%, #00B894 100%);
  padding: 10px 16px;
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: #000;

  strong {
    font-weight: 700;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 8px 12px;
  }
`;

// Main Content
const Main = styled.main`
  flex: 1;
  background: #f5f5f5;
`;

// Footer
const Footer = styled.footer`
  background: #fff;
  border-top: 1px solid #e5e5e5;
  padding: 40px 16px 24px;
  margin-top: auto;
`;

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FooterSection = styled.div`
  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #111;
    margin-bottom: 16px;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  a {
    color: #666;
    text-decoration: none;
    font-size: 13px;
    transition: color 150ms;

    &:hover {
      color: #00D4AA;
    }
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;

  p {
    font-size: 13px;
    color: #666;
    margin-bottom: 8px;
  }

  .input-group {
    display: flex;
    gap: 8px;
  }

  input {
    flex: 1;
    background: #f5f5f5;
    border: 1px solid #e5e5e5;
    padding: 10px 12px;
    border-radius: 4px;
    color: #333;
    font-size: 13px;

    &:focus {
      outline: none;
      border-color: #00D4AA;
    }
  }

  button {
    background: #111;
    color: white;
    border: none;
    padding: 10px 16px;
    border-radius: 4px;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: background 150ms;

    &:hover {
      background: #333;
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid #e5e5e5;
  padding-top: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.div`
  font-size: 12px;
  color: #999;
`;

const PaymentMethods = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  span {
    background: #f5f5f5;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 11px;
    color: #666;
    font-weight: 500;
  }
`;

// ============================================
// COMPONENTE
// ============================================

const ShopLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { itemCount, openCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/busca?q=${encodeURIComponent(searchQuery)}`);
      setMobileSearchOpen(false);
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    alert('Newsletter cadastrado com sucesso!');
  };

  const isActive = (path) => location.pathname === path;

  const categories = [
    { slug: 'robos-colaborativos', name: 'Robos Colaborativos' },
    { slug: 'sensores', name: 'Sensores' },
    { slug: 'acessorios', name: 'Acessorios' },
    { slug: 'kits-educacionais', name: 'Kits Educacionais' }
  ];

  return (
    <LayoutContainer>
      {/* Top Bar */}
      <TopBar>
        <TopBarContainer>
          <TopBarLinks>
            <a href="https://www.vegarobotics.com.br" target="_blank" rel="noopener noreferrer">
              Site Institucional
            </a>
            <a href="https://blog.vegarobotics.com.br" target="_blank" rel="noopener noreferrer">
              Blog
            </a>
          </TopBarLinks>
          <TopBarRight>
            <Link to="/como-comprar">
              <FaQuestionCircle /> Ajuda
            </Link>
            <Link to="/conta">
              <FaUser /> Minha Conta
            </Link>
          </TopBarRight>
        </TopBarContainer>
      </TopBar>

      {/* Header Principal */}
      <Header>
        <HeaderMain>
          <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </MobileMenuButton>

          <Logo to="/">
            <img src={logoVega} alt="Vega Robotics" />
            <LogoBadge>Shop</LogoBadge>
          </Logo>

          <SearchContainer $mobileVisible={mobileSearchOpen}>
            <SearchBar onSubmit={handleSearch}>
              <SearchInput
                type="text"
                placeholder="Buscar produtos, categorias..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <SearchButton type="submit">
                <FaSearch />
              </SearchButton>
            </SearchBar>
          </SearchContainer>

          <HeaderActions>
            <MobileSearchToggle onClick={() => setMobileSearchOpen(!mobileSearchOpen)}>
              <FaSearch />
            </MobileSearchToggle>

            <ActionItem to="/favoritos">
              <FaHeart />
              <span>Favoritos</span>
            </ActionItem>

            <CartButton onClick={openCart}>
              <FaShoppingCart />
              <span>Carrinho</span>
              {itemCount > 0 && <CartBadge>{itemCount > 99 ? '99+' : itemCount}</CartBadge>}
            </CartButton>
          </HeaderActions>
        </HeaderMain>

        {/* Barra de Categorias */}
        <CategoriesBar $isOpen={mobileMenuOpen}>
          <CategoriesContainer>
            <AllCategoriesButton>
              <FaBars /> Categorias <FaChevronDown />
            </AllCategoriesButton>

            <CategoryLink
              to="/"
              className={isActive('/') ? 'active' : ''}
              onClick={() => setMobileMenuOpen(false)}
            >
              Inicio
            </CategoryLink>

            {categories.map(cat => (
              <CategoryLink
                key={cat.slug}
                to={`/categoria/${cat.slug}`}
                className={location.pathname.includes(cat.slug) ? 'active' : ''}
                onClick={() => setMobileMenuOpen(false)}
              >
                {cat.name}
              </CategoryLink>
            ))}
          </CategoriesContainer>
        </CategoriesBar>
      </Header>

      {/* Banner Promocional */}
      <PromoBanner>
        <strong>FRETE GRATIS</strong> para compras acima de R$ 500 | Parcelamento em ate <strong>12x sem juros</strong>
      </PromoBanner>

      {/* Conteudo Principal */}
      <Main>
        <Outlet />
      </Main>

      {/* Footer */}
      <Footer>
        <FooterContainer>
          <FooterGrid>
            <FooterSection>
              <h4>Institucional</h4>
              <FooterLinks>
                <a href="https://www.vegarobotics.com.br" target="_blank" rel="noopener noreferrer">
                  Sobre a Vega
                </a>
                <a href="https://www.vegarobotics.com.br/carreiras" target="_blank" rel="noopener noreferrer">
                  Trabalhe Conosco
                </a>
                <Link to="/contato">Contato</Link>
              </FooterLinks>
            </FooterSection>

            <FooterSection>
              <h4>Ajuda</h4>
              <FooterLinks>
                <Link to="/como-comprar">Como Comprar</Link>
                <Link to="/prazos-entrega">Prazos de Entrega</Link>
                <Link to="/trocas-devolucoes">Trocas e Devolucoes</Link>
                <Link to="/perguntas-frequentes">FAQ</Link>
              </FooterLinks>
            </FooterSection>

            <FooterSection>
              <h4>Minha Conta</h4>
              <FooterLinks>
                <Link to="/conta">Meus Dados</Link>
                <Link to="/conta/pedidos">Meus Pedidos</Link>
                <Link to="/favoritos">Favoritos</Link>
                <Link to="/carrinho">Carrinho</Link>
              </FooterLinks>
            </FooterSection>

            <FooterSection>
              <h4>Newsletter</h4>
              <NewsletterForm onSubmit={handleNewsletterSubmit}>
                <p>Receba novidades e ofertas exclusivas</p>
                <div className="input-group">
                  <input type="email" placeholder="Seu e-mail" required />
                  <button type="submit">OK</button>
                </div>
              </NewsletterForm>
            </FooterSection>
          </FooterGrid>

          <FooterBottom>
            <Copyright>
              © {new Date().getFullYear()} Vega Robotics. Todos os direitos reservados.
            </Copyright>
            <PaymentMethods>
              <span>Pix</span>
              <span>Visa</span>
              <span>Mastercard</span>
              <span>Elo</span>
              <span>Boleto</span>
            </PaymentMethods>
          </FooterBottom>
        </FooterContainer>
      </Footer>

      <CartDrawer />
    </LayoutContainer>
  );
};

export default ShopLayout;
